<template>
  <div class="store-page">
    <div class="container py-4">
      <SectionHeader :label="kind === 'course' ? 'الكورسات' : 'السنوات الدراسية'"
                     :title="pageTitle" :subtitle="pageSubtitle" />

      <!-- Controls -->
      <div class="store-controls">
        <div class="store-search">
          <i class="pi pi-search"></i>
          <input type="text" v-model="search" :placeholder="kind === 'course' ? 'ابحث عن كورس...' : 'ابحث عن سنة دراسية...'" />
          <button v-if="search" class="store-search__clear" @click="search = ''" aria-label="مسح"><i class="pi pi-times"></i></button>
        </div>
        <select class="store-sort" v-model="sort">
          <option value="default">الترتيب الافتراضي</option>
          <option value="name">الاسم (أ - ي)</option>
          <option value="price_asc">السعر: من الأقل</option>
          <option value="price_desc">السعر: من الأعلى</option>
        </select>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="store-grid">
        <CardSkeleton v-for="n in 6" :key="n" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="store-state">
        <i class="pi pi-exclamation-triangle"></i>
        <p>تعذّر تحميل البيانات.</p>
        <button class="store-retry" @click="load"><i class="pi pi-refresh"></i> إعادة المحاولة</button>
      </div>

      <!-- Empty -->
      <div v-else-if="!filtered.length" class="store-state">
        <i class="pi pi-inbox"></i>
        <p>{{ search ? 'لا توجد نتائج مطابقة للبحث.' : 'لا توجد عناصر متاحة حالياً.' }}</p>
      </div>

      <!-- Grid -->
      <template v-else>
        <!-- Featured (if any) -->
        <template v-if="featured.length && !search">
          <h4 class="store-subhead"><i class="pi pi-star-fill"></i> مميّز</h4>
          <div class="store-grid mb-4">
            <ProductCard v-for="item in featured" :key="'f'+item.id" :product="item" :kind="kind"
                         @open="openDetails(item)" @subscribe="subscribe(item)" />
          </div>
          <h4 class="store-subhead">الكل</h4>
        </template>

        <div class="store-grid">
          <ProductCard v-for="item in paged" :key="item.id" :product="item" :kind="kind"
                       @open="openDetails(item)" @subscribe="subscribe(item)" />
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="store-pager">
          <button :disabled="page === 1" @click="page--"><i class="pi pi-angle-right"></i></button>
          <button v-for="p in totalPages" :key="p" :class="{ active: p === page }" @click="page = p">{{ p }}</button>
          <button :disabled="page === totalPages" @click="page++"><i class="pi pi-angle-left"></i></button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import ProductCard from '@/components/subscriptions/ProductCard.vue';
import CardSkeleton from '@/components/subscriptions/CardSkeleton.vue';
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans } from '@/composables/subscriptionMeta.js';

export default {
  name: 'StoreListing',
  components: { SectionHeader, ProductCard, CardSkeleton },
  props: {
    kind: { type: String, default: 'course' }, // 'academic_year' | 'course'
  },
  data() {
    return {
      items: [],
      loading: true,
      error: false,
      search: '',
      sort: 'default',
      page: 1,
      perPage: 9,
    };
  },
  computed: {
    pageTitle() { return this.kind === 'course' ? 'الكورسات المتاحة' : 'السنوات الدراسية'; },
    pageSubtitle() {
      return this.kind === 'course'
        ? 'اشترك في الكورس المناسب لك وابدأ التعلّم فوراً'
        : 'اختر سنتك الدراسية واشترك للوصول إلى كل المحتوى';
    },
    featured() { return this.items.filter(i => i.featured); },
    filtered() {
      let list = [...this.items];
      const q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(i => {
          const name = (this.kind === 'course' ? i.title : i.name) || '';
          const teacher = i.teacher || '';
          return name.toLowerCase().includes(q) || teacher.toLowerCase().includes(q);
        });
      }
      const minPrice = (i) => {
        const prices = productPlans(i).map(p => p.price);
        return prices.length ? Math.min(...prices) : Number.MAX_SAFE_INTEGER;
      };
      if (this.sort === 'name') {
        list.sort((a, b) => (this.name(a)).localeCompare(this.name(b), 'ar'));
      } else if (this.sort === 'price_asc') {
        list.sort((a, b) => minPrice(a) - minPrice(b));
      } else if (this.sort === 'price_desc') {
        list.sort((a, b) => minPrice(b) - minPrice(a));
      } else {
        list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      }
      // The student's own academic year always leads, regardless of the chosen sort —
      // the backend already resolves this from auth; keep it pinned through re-sorts.
      if (this.kind === 'academic_year') {
        list.sort((a, b) => (b.is_students_year ? 1 : 0) - (a.is_students_year ? 1 : 0));
      }
      return list;
    },
    totalPages() { return Math.max(1, Math.ceil(this.filtered.length / this.perPage)); },
    paged() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
  },
  watch: {
    search() { this.page = 1; },
    sort() { this.page = 1; },
    kind() { this.load(); },
  },
  methods: {
    name(i) { return (this.kind === 'course' ? i.title : i.name) || ''; },
    async load() {
      this.loading = true;
      this.error = false;
      this.page = 1;
      try {
        this.items = this.kind === 'course'
          ? await subscriptionService.courses()
          : await subscriptionService.academicYears();
      } catch {
        this.error = true;
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    openDetails(item) {
      const base = this.kind === 'course' ? '/course' : '/academic-year';
      this.$router.push(`${base}/${item.id}`);
    },
    // Mirrors ProductDetails.vue's primaryAction(): the card's CTA label already reflects
    // hasAccess/isFree (ادخل الآن / ابدأ الآن), so the click must route accordingly too.
    subscribe(item) {
      if (item.has_access || !item.is_subscription_enabled) {
        this.$router.push(this.kind === 'academic_year' ? '/contents' : '/my-subscriptions');
        return;
      }
      this.$router.push({ path: '/subscribe', query: { type: this.kind, id: item.id } });
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.store-page { min-height: 60vh; padding-bottom: 5rem; }
.store-controls {
  display: flex; gap: .75rem; flex-wrap: wrap; margin-bottom: 1.5rem;
}
.store-search {
  position: relative; flex: 1; min-width: 200px;
  display: flex; align-items: center;
}
.store-search > .pi-search {
  position: absolute; inset-inline-start: 14px; color: #94a3b8; font-size: .9rem;
}
.store-search input {
  width: 100%; padding: .7rem 2.4rem; border: 1px solid #e2e8f0; border-radius: 12px;
  font-size: .9rem; outline: none; transition: border-color .2s;
}
.store-search input:focus { border-color: #5B5FEF; }
.store-search__clear {
  position: absolute; inset-inline-end: 10px; background: none; border: none; color: #94a3b8; cursor: pointer;
}
.store-sort {
  border: 1px solid #e2e8f0; border-radius: 12px; padding: .7rem 1rem; font-size: .88rem; outline: none;
  background: #fff; min-width: 180px;
}
/* Mobile-first grid: 1 card per row on phones, 2 on tablets, 3–4 on desktop —
   never a shrunk desktop card squeezed side-by-side on a phone screen. */
.store-grid {
  display: grid; gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}
@media (max-width: 1023px) {
  .store-grid { grid-template-columns: repeat(2, 1fr); gap: 1.25rem; }
}
@media (max-width: 640px) {
  .store-grid { grid-template-columns: 1fr; gap: 1.1rem; }
}
.store-subhead { font-size: 1rem; font-weight: 800; color: #374151; margin: .5rem 0 1rem; }
.store-subhead .pi-star-fill { color: #f59e0b; }
.store-state {
  text-align: center; padding: 3.5rem 1rem; color: #94a3b8;
}
.store-state .pi { font-size: 2.6rem; margin-bottom: .8rem; display: block; }
.store-retry {
  margin-top: .5rem; background: #5B5FEF; color: #fff; border: none;
  border-radius: 10px; padding: .5rem 1.1rem; font-weight: 700; cursor: pointer;
}
.store-pager {
  display: flex; justify-content: center; gap: .4rem; margin-top: 2rem; flex-wrap: wrap;
}
.store-pager button {
  min-width: 38px; height: 38px; border: 1px solid #e2e8f0; background: #fff;
  border-radius: 10px; font-weight: 700; color: #374151; cursor: pointer;
}
.store-pager button.active { background: #5B5FEF; color: #fff; border-color: #5B5FEF; }
.store-pager button:disabled { opacity: .4; cursor: not-allowed; }
</style>
